import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react"; // Optional: Use Lucide for better icons
import "./HomeSlider.css";

const CombinedUpdates = () => {
  // --- State ---
  const [newsData, setNewsData] = useState([]);
  const [columnData, setColumnData] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newsIndex, setNewsIndex] = useState(0);
  const [columnIndex, setColumnIndex] = useState(0);
  
  const [itemWidths, setItemWidths] = useState({ news: 0, column: 0 });
  const [perView, setPerView] = useState(4);

  // --- Refs ---
  const newsSliderRef = useRef(null);
  const newsContainerRef = useRef(null);
  const columnSliderRef = useRef(null);
  const columnContainerRef = useRef(null);
  const newsAutoPlayRef = useRef(null);
  const columnAutoPlayRef = useRef(null);

  // 1. Unified Data Fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [newsRes, colRes] = await Promise.all([
          fetch("https://mirai-server.vercel.app/gallery-data"),
          fetch("https://mirai-server.vercel.app/column-update")
        ]);
        setNewsData(await newsRes.json());
        setColumnData(await colRes.json());
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // 2. Responsive Measurement Logic
  useLayoutEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      let items = 4;
      if (width < 640) items = 1.2; // Show a peek of the next card on mobile
      else if (width < 1024) items = 2;
      else if (width < 1280) items = 3;

      setPerView(items);

      const nW = newsContainerRef.current?.offsetWidth / items || 0;
      const cW = columnContainerRef.current?.offsetWidth / items || 0;
      setItemWidths({ news: nW, column: cW });
    };

    if (!loading) {
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [loading, newsData.length, columnData.length]);

  // 3. GSAP Animation Logic
  useEffect(() => {
    if (newsSliderRef.current) {
      gsap.to(newsSliderRef.current, {
        x: -(newsIndex * itemWidths.news),
        duration: 0.8,
        ease: "expo.out"
      });
    }
  }, [newsIndex, itemWidths.news]);

  useEffect(() => {
    if (columnSliderRef.current) {
      gsap.to(columnSliderRef.current, {
        x: -(columnIndex * itemWidths.column),
        duration: 0.8,
        ease: "expo.out"
      });
    }
  }, [columnIndex, itemWidths.column]);

  // 4. Autoplay
  useEffect(() => {
    const startAutoplay = (data, setIdx, ref, delay) => {
      ref.current = setInterval(() => {
        setIdx(prev => (prev >= data.length - Math.floor(perView) ? 0 : prev + 1));
      }, delay);
    };

    if (newsData.length > perView) startAutoplay(newsData, setNewsIndex, newsAutoPlayRef, 5000);
    if (columnData.length > perView) startAutoplay(columnData, setColumnIndex, columnAutoPlayRef, 6000);

    return () => {
      clearInterval(newsAutoPlayRef.current);
      clearInterval(columnAutoPlayRef.current);
    };
  }, [newsData, columnData, perView]);

  if (loading) {
    return (
      <div className="h-96 flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="font-jost text-blue-900 font-medium">Loading Experience...</p>
        </div>
      </div>
    );
  }

  const SliderBtn = ({ dir, onClick }) => (
    <button
      onClick={onClick}
      className={`absolute ${dir === 'l' ? '-left-4' : '-right-4'} top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-white/90 backdrop-blur-md shadow-xl flex items-center justify-center text-blue-900 hover:bg-blue-900 hover:text-white transition-all duration-300 border border-blue-50 group`}
    >
      {dir === 'l' ? <ChevronLeft size={24} /> : <ChevronRight size={24} />}
    </button>
  );

  return (
    <div className="bg-[#f8fafc] overflow-x-hidden">
      {/* SECTION 1: LATEST NEWS */}
      <section className="py-16 lg:py-24 border-b border-blue-50">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          
          {/* Header with Title and View All */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 lg:mb-16">
            <div className="space-y-2">
              <span className="text-blue-600 font-bold tracking-widest uppercase text-xs">Stay Informed</span>
              <h2 className="text-xl lg:text-6xl font-bold font-jost text-slate-900 tracking-tight">Latest News</h2>
            </div>
            <Link 
              to="/gallery" 
              className="group flex items-center gap-2 text-blue-600 font-bold text-md hover:text-blue-800 transition-colors"
            >
              View All Updates
              <div className="p-2 bg-blue-50 rounded-full group-hover:translate-x-1 transition-transform">
                <ArrowRight size={18} />
              </div>
            </Link>
          </div>

          <div className="relative group/slider" ref={newsContainerRef}>
            <SliderBtn dir="l" onClick={() => { setNewsIndex(p => Math.max(0, p-1)); clearInterval(newsAutoPlayRef.current); }} />
            <SliderBtn dir="r" onClick={() => { setNewsIndex(p => (p >= newsData.length - Math.ceil(perView) ? 0 : p + 1)); clearInterval(newsAutoPlayRef.current); }} />

            <div className="overflow-visible lg:overflow-hidden">
              <div ref={newsSliderRef} className="flex flex-row items-stretch">
                {newsData.map((item, idx) => (
                  <div key={item._id || idx} className="px-3 lg:px-4 shrink-0" style={{ width: itemWidths.news }}>
                    <Link to={`/gallery-details/${item._id}`} className="group block h-full">
                      <div className="bg-white rounded-[2rem] shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 overflow-hidden h-full flex flex-col border border-slate-100">
                        <div className="aspect-[4/3] overflow-hidden relative">
                          <img src={item.singleImage} alt="" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                        </div>
                        <div className="p-6 lg:p-8 flex-grow flex flex-col">
                          <h3 className="text-xl lg:text-2xl font-jost text-slate-800 font-bold line-clamp-2 leading-tight group-hover:text-blue-600 transition-colors">{item.title}</h3>
                          <div className="mt-auto pt-6 flex items-center text-blue-500 font-bold text-sm tracking-wide uppercase">
                            Read Article <ArrowRight size={16} className="ml-2 group-hover:translate-x-2 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: COLUMN UPDATES */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
                   {/* Header with Title and View All */}
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 lg:mb-16">
            <div className="space-y-2">
              <span className="text-blue-600 font-bold tracking-widest uppercase text-xs">Expert Perspectives</span>
              <h2 className="text-xl lg:text-6xl font-bold font-jost text-slate-900 tracking-tight">Columns</h2>
            </div>
  
          </div>
          <div className="relative" ref={columnContainerRef}>
            <SliderBtn dir="l" onClick={() => { setColumnIndex(p => Math.max(0, p-1)); clearInterval(columnAutoPlayRef.current); }} />
            <SliderBtn dir="r" onClick={() => { setColumnIndex(p => (p >= columnData.length - Math.ceil(perView) ? 0 : p + 1)); clearInterval(columnAutoPlayRef.current); }} />

            <div className="overflow-hidden">
              <div ref={columnSliderRef} className="flex flex-row items-stretch">
                {columnData.map((item, idx) => (
                  <div key={item._id || idx} className="px-3 shrink-0" style={{ width: itemWidths.column }}>
                    <Link to={`/news-details/${item._id}`} className="block h-full group">
                      <div className="bg-[#fcfdfe] rounded-2xl p-4 h-full border border-slate-100 group-hover:bg-blue-50/30 transition-colors">
                        <div className="aspect-[16/9] rounded-xl overflow-hidden mb-5">
                          <img src={item.singleImage} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                        <h3 className="text-lg font-jost text-slate-800 font-semibold line-clamp-2 group-hover:text-blue-700 transition-colors leading-snug">{item.title}</h3>
                        <p className="mt-3 text-slate-500 text-sm line-clamp-2">Insights and expert takes on the latest industry trends...</p>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CombinedUpdates;